import ComingSoonStats from '@/components/game-result/ComingSoonStats';
import { FairwayAccuracy } from '@/components/game-result/FairwayAccuracy';
import ScoreByPar from '@/components/game-result/ScoreByPar';
import ScoreDistribution from '@/components/game-result/ScoreDistribution';
import { useUserProfile } from '@/context/UserProfileContext';
import { AggregatedStats } from '@/types';
import { api } from '@/utils/api';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonLabel,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonTitle,
  IonToolbar
} from '@ionic/react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

// Default initial stats state
const defaultStats: AggregatedStats = {
  totalGames: 0,
  avgScore: 0,
  handicap: 0,
  eagles: 0,
  birdies: 0,
  pars: 0,
  bogeys: 0,
  doubleBogeys: 0,
  totalPutts: 0,
  holesWithPuttsData: 0,
  fairwaysHitCenter: 0,
  fairwayOpportunities: 0,
  greensInRegulationCount: 0,
  girOpportunities: 0,
  par3Score: {
    total: 0,
    toPar: 0,
    count: 0
  },
  par4Score: {
    total: 0,
    toPar: 0,
    count: 0
  },
  par5Score: {
    total: 0,
    toPar: 0,
    count: 0
  }
}

interface UserData {
  userId: string;
  username: string;
  handicap?: number | null;
  profilePicture?: string;
}

const UserStatsPage: React.FC = () => {
  const { t } = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const { profile } = useUserProfile();
  const [selectedSegment, setSelectedSegment] = useState<'all' | 'recent'>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [stats, setStats] = useState<AggregatedStats>(defaultStats);
  const [error, setError] = useState<string | null>(null);

  const isCurrentUser = !userId || (profile && userId === profile.userId);

  // Fetch user data if viewing another user's stats
  useEffect(() => {
    if (isCurrentUser && profile) {
      // Use current user's profile data
      setUserData({
        userId: profile.userId,
        username: profile.username,
        handicap: profile.handicap,
        profilePicture: profile.imageUrl // Use avatar property instead of profilePicture
      });
      return;
    }

    if (!userId) return;

    document.title = isCurrentUser ? t('stats') : `${userData?.username}'s ${t('stats')}`;

    const fetchUserData = async () => {
      try {
        const response = await api.get(`/users/${userId}`);
        if (response.data) {
          setUserData({
            userId: response.data.userId,
            username: response.data.username,
            handicap: response.data.handicap,
            profilePicture: response.data.profilePicture
          });
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        setError(t('cant_load_profile'));
      }
    };

    fetchUserData();
  }, [userId, profile, isCurrentUser, t]);

  // Fetch stats data
  useEffect(() => {
    if (!userData) return;

    const fetchStats = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Use dedicated stats endpoint with timeframe parameter
        const timeframeParam = selectedSegment === 'recent' ? '?timeframe=recent' : '';
        const response = await api.get(`/stats/${userData.userId}${timeframeParam}`);

        if (response.data) {
          // Server returns pre-aggregated stats
          setStats({
            ...response.data,
            handicap: userData?.handicap || 0 // Use the user's current handicap
          });
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
        setError(t('error_loading_stats', 'Error loading statistics'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, [userData, selectedSegment, t]);

  const handleSegmentChange = (e: CustomEvent) => {
    const newSegment = e.detail.value as 'all' | 'recent';
    setSelectedSegment(newSegment);
  };

  const getPageTitle = () => {
    if (isCurrentUser) {
      return t('insights');
    }
    return userData ? `${userData.username}'s ${t('insights').toLowerCase()}` : t('insights');
  };

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar>
          {!isCurrentUser && (
            <IonButtons slot="start">
              <IonBackButton defaultHref={`/u/${userId}`} />
            </IonButtons>
          )}
          <IonTitle>{getPageTitle()}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        {isLoading && !userData ? (
          <div className="flex justify-center items-center h-full">
            <IonSpinner />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full p-4 text-center">
            <img src="/assets/images/notfound.svg" alt="" className="size-64 mx-auto mb-4" />
            <h3 className="text-xl font-medium">{t('oops')}</h3>
            <p className="text-muted-foreground mt-2">{error}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Player handicap card */}
            <div className="max-w-2xl">
              <div className="stats bg-card w-full">
                <div className="stat">
                  <div className="stat-title text-foreground">{t('golf_handicap')}</div>
                  <div className="stat-value text-foreground">{userData?.handicap ? userData.handicap : '-'}</div>
                </div>

                <div className="stat">
                  <div className="stat-title text-foreground">{t('played_rounds')}</div>
                  <div className="stat-value text-foreground">{stats.totalGames ? stats.totalGames : '-'}</div>
                </div>

                <div className="stat">
                  <div className="stat-title text-foreground">{t('average_score')}</div>
                  <div className="stat-value text-foreground">{stats.avgScore ? stats.avgScore : '-'}</div>
                </div>
              </div>
            </div>

            {/* Segment for All Games vs Recent Games */}
            <IonSegment
              value={selectedSegment}
              onIonChange={handleSegmentChange}
            >
              <IonSegmentButton value="all">
                <IonLabel>{t('all_games')}</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="recent">
                <IonLabel>{t('last_20')}</IonLabel>
              </IonSegmentButton>
            </IonSegment>

            {/* No stats message */}
            {stats.totalGames === 0 && (
              <div className="text-center p-8">
                <img src="/assets/images/notfound.svg" alt="" className="size-64 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-medium">
                  {isCurrentUser
                    ? t('no_stats_title', 'No stats yet')
                    : t('no_user_stats_title', 'No stats available')}
                </h3>
                <p className="text-muted-foreground mt-2">
                  {isCurrentUser
                    ? t('no_golf_round_caption')
                    : t('no_user_golf_round_caption', `${userData?.username} hasn't played any rounds yet`)}
                </p>
              </div>
            )}

            {/* Statistics Grid */}
            {stats.totalGames > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ScoreDistribution stats={stats} />
                <FairwayAccuracy stats={stats} />
                <ScoreByPar stats={stats} />
                <div className="w-full h-[288px] bg-card rounded-2xl">
                  <ComingSoonStats className="size-full" />
                </div>
              </div>
            )}
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default UserStatsPage;
